import { normalizeCardJsonV1 } from '../../data/cardsApi'
import type { DeckCardInput } from '../../deck/types'
import type { FlashcardElementInput } from '../../flashcard/elements/registry'
import type { DeckPlayerBootstrapV1 } from '../types'

type Bootstrap = DeckPlayerBootstrapV1 | null | undefined

/** Card border width from the editor's props; `borderEnabled` wins over `borderWidth`. */
const borderWidthOf = (p: Record<string, unknown>) => {
  if (p.borderEnabled === false) return 0
  if (p.borderEnabled === true) {
    return Math.max(0, typeof p.borderWidth === 'number' ? p.borderWidth : 1)
  }
  return typeof p.borderWidth === 'number' ? Math.max(0, p.borderWidth) : undefined
}

/** CSS box-shadow from the editor's shadow props (explicit `shadow` string wins). */
const shadowOf = (p: Record<string, unknown>) => {
  if (typeof p.shadow === 'string') return p.shadow
  if (p.shadowEnabled === false) return 'none'
  if (p.shadowEnabled === true) {
    const num = (key: string, fallback: number) =>
      typeof p[key] === 'number' ? (p[key] as number) : fallback
    const color = typeof p.shadowColor === 'string' ? p.shadowColor : 'rgba(15, 23, 42, 0.18)'
    return `${num('shadowX', 0)}px ${num('shadowY', 0)}px ${num('shadowBlur', 16)}px ${num('shadowSpread', -6)}px ${color}`
  }
  return undefined
}

const optional = <T extends 'string' | 'number'>(
  p: Record<string, unknown>,
  key: string,
  type: T,
) =>
  (typeof p[key] === type ? p[key] : undefined) as
    (T extends 'string' ? string : number) | undefined

/** Player cards from the bootstrap's stored card documents (invalid ones are skipped). */
export function toDeckCards(bootstrap: Bootstrap): DeckCardInput[] {
  return (bootstrap?.cards ?? [])
    .map((item) => {
      const normalized = normalizeCardJsonV1<FlashcardElementInput>(item.json)
      if (!normalized) return null
      const p = normalized.props as Record<string, unknown>
      const number = (key: string, fallback: number) =>
        typeof p[key] === 'number' && Number.isFinite(p[key] as number)
          ? (p[key] as number)
          : fallback
      return {
        id: item.id,
        postId: item.postId,
        frontElements: normalized.frontElements,
        backElements: normalized.backElements,
        width: number('width', 240),
        height: number('height', 160),
        darkMode: Boolean(p.darkMode),
        backgroundColor: optional(p, 'backgroundColor', 'string'),
        themeBackgroundColor: optional(p, 'themeBackgroundColor', 'string'),
        borderRadius: number('borderRadius', 16),
        themeBorderRadius: optional(p, 'themeBorderRadius', 'number'),
        borderColor: optional(p, 'borderColor', 'string'),
        themeBorderColor: optional(p, 'themeBorderColor', 'string'),
        borderWidth: borderWidthOf(p),
        shadow: shadowOf(p),
        transitionDuration: optional(p, 'transitionDuration', 'number'),
        themeTransitionDuration: optional(p, 'themeTransitionDuration', 'number'),
        flipAnimation: optional(p, 'flipAnimation', 'string') as DeckCardInput['flipAnimation'],
        themeFlipAnimation: optional(
          p,
          'themeFlipAnimation',
          'string',
        ) as DeckCardInput['themeFlipAnimation'],
        hoverFlip: Boolean(p.hoverFlip),
        showBackFirst: Boolean(p.showBackFirst),
      } satisfies DeckCardInput
    })
    .filter(Boolean) as DeckCardInput[]
}

/**
 * Saved checkbox values per card. Progress may be keyed by post id while player
 * cards use ids like "card-123", so values are stored under every matching key.
 */
export function progressCheckboxValues(bootstrap: Bootstrap): Record<string, boolean[]> {
  const progressCards = bootstrap?.progress?.cards
  if (!progressCards) return {}

  const idByPostId: Record<string, string> = {}
  for (const item of bootstrap?.cards ?? []) {
    const { postId, id } = item as { postId?: unknown; id?: unknown }
    if (typeof postId === 'number' && Number.isFinite(postId) && typeof id === 'string' && id) {
      idByPostId[String(postId)] = id
    }
  }

  const out: Record<string, boolean[]> = {}
  const addCandidate = (key: string | null, values: boolean[]) => {
    const k = key?.trim()
    if (!k) return
    out[k] = values
    if (/^\d+$/.test(k)) {
      out[`card-${k}`] = values
      const mapped = idByPostId[k]
      if (mapped) out[mapped] = values
    }
  }

  for (const [key, value] of Object.entries(progressCards)) {
    const rawId = (value as { cardId?: unknown }).cardId
    const cardId =
      typeof rawId === 'string' ? rawId : typeof rawId === 'number' ? String(rawId) : null
    const values = Array.isArray(value.checkboxState?.values)
      ? value.checkboxState.values.map(Boolean)
      : []
    addCandidate(key, values)
    addCandidate(cardId, values)
    if (cardId && idByPostId[cardId]) out[idByPostId[cardId]!] = values
  }
  return out
}

/**
 * Elementor "custom savepoint": start at a fixed phase with N boxes ticked on every
 * card, without writing progress. Null when not enabled.
 */
export function customSavepointConfig(passthrough: Record<string, unknown> | undefined) {
  const raw = passthrough ?? {}
  const flag = raw.customSavepoint
  const enabled = flag === true || flag === 1 || flag === 'yes' || flag === 'true' || flag === '1'
  if (!enabled) return null
  const phaseId =
    typeof raw.initialPhaseId === 'string' && raw.initialPhaseId.trim()
      ? raw.initialPhaseId.trim()
      : null
  const count = raw.initialCheckboxCount
  return { phaseId, checkboxCount: typeof count === 'number' && count >= 0 ? Math.floor(count) : 0 }
}

export type CustomSavepoint = NonNullable<ReturnType<typeof customSavepointConfig>>

/** Checkbox values the player starts with: a savepoint, or saved (flow) progress. */
export function initialCheckboxValues(
  bootstrap: Bootstrap,
  cards: DeckCardInput[],
  savepoint: CustomSavepoint | null,
): Record<string, boolean[]> {
  if (savepoint) {
    if (savepoint.checkboxCount === 0) return {}
    const values = Array.from({ length: savepoint.checkboxCount }, () => true)
    const out: Record<string, boolean[]> = {}
    for (const card of cards) {
      out[card.id] = values
      if (card.postId) {
        const postId = String(card.postId)
        out[postId] = values
        out[`card-${postId}`] = values
      }
    }
    return out
  }
  const base = progressCheckboxValues(bootstrap)
  // Flowchart decks also restore the checkbox states saved with their phase.
  const flowStates = bootstrap?.progress?.flowProgress?.checkboxStates
  if (flowStates && typeof flowStates === 'object') {
    for (const [cardId, values] of Object.entries(flowStates)) {
      if (Array.isArray(values) && cardId) base[cardId] = values.map(Boolean)
    }
  }
  return base
}

/** Phase a flowchart resumes at: the savepoint's, else the saved one. */
export function initialFlowPhaseId(
  bootstrap: Bootstrap,
  savepoint: CustomSavepoint | null,
): string | null {
  if (savepoint) return savepoint.phaseId
  const phaseId = bootstrap?.progress?.flowProgress?.phaseId
  return typeof phaseId === 'string' && phaseId.trim() ? phaseId.trim() : null
}

/** Deck flow setting; replaying completed decks is allowed unless explicitly disabled. */
export function allowReplayWhenCompleted(bootstrap: Bootstrap): boolean {
  const flow = bootstrap?.deck?.flow
  if (!flow || typeof flow !== 'object') return true
  const completion = (flow as Record<string, unknown>).completion
  if (!completion || typeof completion !== 'object' || Array.isArray(completion)) return true
  const value = (completion as Record<string, unknown>).allowReplayWhenCompleted
  return typeof value === 'boolean' ? value : true
}

/** Whether a retry is currently allowed by the backend's cooldown. */
export function retryCooldown(bootstrap: Bootstrap, now: number) {
  const cooldown = bootstrap?.progress?.cooldown
  if (!cooldown) return { enforced: false, nextAllowedAt: null as string | null, allowed: true }
  const enforced = cooldown.enforced === true
  const nextAllowedAt =
    typeof cooldown.nextAllowedAt === 'string' && cooldown.nextAllowedAt.trim().length
      ? cooldown.nextAllowedAt
      : null
  if (!enforced || !nextAllowedAt) return { enforced, nextAllowedAt, allowed: true }
  const ms = new Date(nextAllowedAt).getTime()
  if (!Number.isFinite(ms)) return { enforced, nextAllowedAt, allowed: true }
  return { enforced, nextAllowedAt, allowed: now >= ms }
}
